import { Stock, ChartData, TechnicalIndicators, NewsItem, AIInsight, WatchlistItem } from '../types';

// Mock stock data
export const mockStocks: Record<string, Stock> = {
  AAPL: {
    symbol: 'AAPL',
    name: 'Apple Inc.',
    price: 189.95,
    change: 2.45,
    changePercent: 1.31,
    volume: 45678900,
    marketCap: 2980000000000,
    pe: 29.8,
    high52Week: 199.62,
    low52Week: 124.17
  },
  NVDA: {
    symbol: 'NVDA',
    name: 'NVIDIA Corporation',
    price: 875.28,
    change: -12.45,
    changePercent: -1.40,
    volume: 28456700,
    marketCap: 2160000000000,
    pe: 65.4,
    high52Week: 974.00,
    low52Week: 180.96
  },
  TSLA: {
    symbol: 'TSLA',
    name: 'Tesla, Inc.',
    price: 248.50,
    change: 8.75,
    changePercent: 3.65,
    volume: 89234500,
    marketCap: 790000000000,
    pe: 62.1,
    high52Week: 299.29,
    low52Week: 101.81
  },
  MSFT: {
    symbol: 'MSFT',
    name: 'Microsoft Corporation',
    price: 378.85,
    change: -1.25,
    changePercent: -0.33,
    volume: 23456700,
    marketCap: 2810000000000,
    pe: 32.5,
    high52Week: 384.30,
    low52Week: 213.43
  },
  GOOGL: {
    symbol: 'GOOGL',
    name: 'Alphabet Inc.',
    price: 138.21,
    change: 0.95,
    changePercent: 0.69,
    volume: 18765400,
    marketCap: 1750000000000,
    pe: 25.8,
    high52Week: 153.78,
    low52Week: 83.34
  }
};

// Generate mock chart data
export const generateMockChartData = (symbol: string, days: number = 30): ChartData[] => {
  const data: ChartData[] = [];
  const basePrice = mockStocks[symbol]?.price || 100;
  let currentPrice = basePrice * 0.9; // Start 10% below current price
  
  for (let i = days; i >= 0; i--) {
    const timestamp = Date.now() - (i * 24 * 60 * 60 * 1000);
    const volatility = 0.02; // 2% daily volatility
    const change = (Math.random() - 0.5) * volatility;
    
    const open = currentPrice;
    const close = currentPrice * (1 + change);
    const high = Math.max(open, close) * (1 + Math.random() * 0.01);
    const low = Math.min(open, close) * (1 - Math.random() * 0.01);
    const volume = Math.floor(Math.random() * 50000000) + 10000000;
    
    data.push({
      timestamp,
      open,
      high,
      low,
      close,
      volume
    });
    
    currentPrice = close;
  }
  
  return data;
};

// Generate sparkline data for watchlist
export const generateSparklineData = (symbol: string): number[] => {
  const data: number[] = [];
  const basePrice = mockStocks[symbol]?.price || 100;
  let currentPrice = basePrice * 0.98;
  
  for (let i = 0; i < 20; i++) {
    const change = (Math.random() - 0.5) * 0.01;
    currentPrice = currentPrice * (1 + change);
    data.push(currentPrice);
  }
  
  return data;
};

// Mock technical indicators
export const mockTechnicalIndicators: Record<string, TechnicalIndicators> = {
  AAPL: {
    rsi: 58.4,
    macd: {
      macd: 1.23,
      signal: 0.89,
      histogram: 0.34
    },
    movingAverages: {
      sma20: 185.67,
      sma50: 178.23,
      ema12: 187.45,
      ema26: 182.11
    }
  },
  NVDA: {
    rsi: 72.1,
    macd: {
      macd: -2.45,
      signal: -1.78,
      histogram: -0.67
    },
    movingAverages: {
      sma20: 892.34,
      sma50: 845.67,
      ema12: 885.23,
      ema26: 867.89
    }
  },
  TSLA: {
    rsi: 45.8,
    macd: {
      macd: 3.45,
      signal: 2.12,
      histogram: 1.33
    },
    movingAverages: {
      sma20: 242.67,
      sma50: 235.89,
      ema12: 246.78,
      ema26: 240.45
    }
  }
};

// Mock news data - Updated for November 2025
export const mockNews: Record<string, NewsItem[]> = {
  AAPL: [
    {
      id: '1',
      title: 'Apple Unveils Revolutionary AI-Powered iPhone 17 Series with Neural Processing',
      summary: 'Apple Inc. announced the iPhone 17 lineup featuring advanced AI capabilities and neural processing chips, driving stock to new highs.',
      url: '#',
      publishedAt: '2025-11-26T10:30:00Z',
      source: 'Reuters',
      sentiment: 'bullish'
    },
    {
      id: '2',
      title: 'Apple Vision Pro 2 Sales Surge as Metaverse Adoption Accelerates',
      summary: 'Second-generation Vision Pro headset sees unprecedented demand as enterprise and consumer adoption of AR/VR technology explodes.',
      url: '#',
      publishedAt: '2025-11-25T14:20:00Z',
      source: 'Bloomberg',
      sentiment: 'bullish'
    },
    {
      id: '3',
      title: 'Apple Services Revenue Hits Record $25B Quarterly, AI Subscriptions Soar',
      summary: 'Apple\'s services division reports record quarterly revenue driven by AI-powered subscription services and App Store growth.',
      url: '#',
      publishedAt: '2025-11-24T09:15:00Z',
      source: 'Financial Times',
      sentiment: 'bullish'
    }
  ],
  NVDA: [
    {
      id: '4',
      title: 'NVIDIA Blackwell Ultra Chips Break Performance Records, Stock Soars',
      summary: 'NVIDIA\'s latest Blackwell Ultra AI chips deliver 10x performance improvements, driving massive data center orders and stock rally.',
      url: '#',
      publishedAt: '2025-11-26T11:45:00Z',
      source: 'TechCrunch',
      sentiment: 'bullish'
    },
    {
      id: '5',
      title: 'NVIDIA Data Center Revenue Hits $35B Quarterly as AI Boom Continues',
      summary: 'Record-breaking quarterly revenue driven by insatiable demand for AI training and inference infrastructure worldwide.',
      url: '#',
      publishedAt: '2025-11-25T16:30:00Z',
      source: 'CNBC',
      sentiment: 'bullish'
    },
    {
      id: '6',
      title: 'NVIDIA Partners with Major Automakers for Autonomous Vehicle AI',
      summary: 'Strategic partnerships with Tesla, Mercedes, and BMW for next-generation autonomous driving AI accelerate growth prospects.',
      url: '#',
      publishedAt: '2025-11-24T13:20:00Z',
      source: 'Wall Street Journal',
      sentiment: 'bullish'
    }
  ],
  TSLA: [
    {
      id: '7',
      title: 'Tesla Full Self-Driving Achieves Level 5 Autonomy, Regulatory Approval Pending',
      summary: 'Tesla announces breakthrough in autonomous driving technology with Level 5 capability, awaiting global regulatory approval.',
      url: '#',
      publishedAt: '2025-11-26T09:30:00Z',
      source: 'Reuters',
      sentiment: 'bullish'
    },
    {
      id: '8',
      title: 'Tesla Gigafactory Texas Reaches 2M Vehicle Annual Production Capacity',
      summary: 'Tesla\'s Texas facility achieves record production milestone, significantly boosting global manufacturing capacity.',
      url: '#',
      publishedAt: '2025-11-25T12:15:00Z',
      source: 'Bloomberg',
      sentiment: 'bullish'
    },
    {
      id: '9',
      title: 'Tesla Energy Storage Business Surpasses $10B Annual Revenue',
      summary: 'Tesla\'s energy storage and solar business becomes major revenue driver as grid storage demand explodes globally.',
      url: '#',
      publishedAt: '2025-11-24T15:45:00Z',
      source: 'Financial Times',
      sentiment: 'bullish'
    }
  ]
};

// Mock AI insights
export const mockAIInsights: Record<string, AIInsight> = {
  AAPL: {
    sentiment: 'bullish',
    confidence: 78,
    recommendation: 'BUY',
    reasoning: 'Strong earnings beat, robust iPhone sales, and expanding services revenue. RSI indicates room for growth.',
    riskLevel: 'MEDIUM'
  },
  NVDA: {
    sentiment: 'neutral',
    confidence: 65,
    recommendation: 'HOLD',
    reasoning: 'While AI demand remains strong, RSI above 70 suggests overbought conditions. Consider waiting for pullback.',
    riskLevel: 'HIGH'
  },
  TSLA: {
    sentiment: 'bullish',
    confidence: 82,
    recommendation: 'BUY',
    reasoning: 'Strong momentum with MACD bullish crossover. Recent price action shows renewed investor interest.',
    riskLevel: 'MEDIUM'
  }
};

// Create watchlist items with sparkline data
export const createWatchlistItems = (symbols: string[]): WatchlistItem[] => {
  return symbols.map(symbol => ({
    ...mockStocks[symbol],
    sparklineData: generateSparklineData(symbol)
  }));
};